<template>
  <div>
    <mail-dialog
      name="send-missing-files"
      :title="$t('Missing files')"
      :description="$t('Send a request for files to the person in charge.')"
      :company="company"
      :project="missingFilesItem && missingFilesItem.project"
      @confirm="onConfirmSendMissingFilesReminder"
      @cancel="onCancelSendMissingFilesReminder"
    />

    <mail-dialog
      name="send-registration-link"
      :title="$t('Mail registration link')"
      :description="$t('Mail link for project {project}', { project: mailLinkItem && mailLinkItem.project.title })"
      :company="company"
      :project="mailLinkItem && mailLinkItem.project"
      @confirm="onMailLinkConfirm"
      @cancel="onMailLinkCancel"
    />

    <b-card class="projects-list shadow">
      <b-table
        hover
        striped
        single
        primary-key="projectId"
        :items="items"
        :fields="fields"
        :empty-text="$t('No items in the list')"
        tbody-tr-class="projects-row"
        class="projects-table mb-0"
        @row-clicked="onRowClick"
      >
        <template #cell(detailsToggle)="{ detailsShowing, item }">
          <b-icon
            :icon="detailsShowing ? 'caret-down-fill' : 'caret-right-fill'"
            @click="toggleDetails(item.projectId)"
          />
        </template>

        <template #cell(title)="{ item }">
          <span v-tooltip="item.tooltip">{{ item.project.title }}</span>
        </template>

        <template #cell(status)="{ item }">
          <b-button
            v-if="item.worstStatus === fileStatuses.NOT_LOADED"
            :title="$t('Send reminder')"
            :class="['files-status', fileStatusClasses[item.worstStatus]]"
            @click.stop="onStatusClick(item)"
          >
            {{ fileStatusTitles[item.worstStatus] }}
          </b-button>
          <div v-else :class="['files-status', fileStatusClasses[item.worstStatus]]">
            {{ fileStatusTitles[item.worstStatus] }}
          </div>
        </template>

        <template #cell(expires)="{ item }">
          <span v-if="item.link" :class="['expiry-link', { 'link-expired': item.isLinkExpired }]">
            {{ $t('Expires {expiryDate}', { expiryDate: item.link.expiryDate }) }}
          </span>
        </template>

        <template #cell(divider)>
          <div class="vertical-line" />
        </template>

        <template #cell(links)="{ item }">
          <template v-if="item.link">
            <template v-if="item.link.link">
              <a :href="getFullLink(item.link.link)" target="_blank" class="mr-3">
                <b-iconstack class="link-icon">
                  <b-icon icon="square" />
                  <b-icon icon="arrow-up-right" scale="0.75" />
                </b-iconstack>
                <span>{{ $t('Go to site') }}</span>
              </a>

              <a class="mr-3" @click="onCopyLinkClick(item.link.link)">
                <b-icon icon="files" class="link-icon mr-1" flip-h />
                <span>{{ $t('Copy') }}</span>
              </a>

              <a class="mr-3" @click="onMailLinkClick(item)">
                <b-iconstack v-if="item.link.linkMailTo" v-tooltip="item.linkTooltip">
                  <b-icon stacked icon="arrow-clockwise" animation="spin" scale="1.5" />
                  <b-icon stacked icon="envelope" scale="0.6" />
                </b-iconstack>
                <b-icon v-else icon="envelope" v-tooltip="item.linkTooltip" class="link-icon mr-1" />
                <span>{{ $t('Mail') }}</span>
              </a>

              <a
                v-if="qrCodes[item.projectId] && qrCodes[item.projectId].href"
                :href="qrCodes[item.projectId].href"
                :download="qrCodes[item.projectId].fileName"
                class="mr-3"
              >
                <b-icon icon="download" class="link-icon mr-1" />
                <span>{{ $t('QR Code') }}</span>
              </a>
            </template>

            <span v-else>
              <b-icon icon="arrow-repeat" animation="spin" />
              <span>{{ $t('Generating link for {project}', { project: item.project.title }) }}</span>
            </span>
          </template>
        </template>

        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle" />
            <strong>{{ $t('Loading...') }}'</strong>
          </div>
        </template>

        <template #row-details="{ item /* , detailsShowing */ }">
          <!-- /** @todo detailsShowing doesn't work */-->
          <project-files v-if="item._showDetails" :company-id="company._id" :project-id="item.projectId" />
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import moment from 'moment';
import QRCode from 'qrcode';

import constants from '@/constants';

import { EventBus } from '@/common/eventbus';
import { byCompanyId as getCompanyFilesByCompanyId } from '@/store/companyFiles/getterTypes';
import { mapCompanyFilesGetters } from '@/store/companyFiles/namespace';

import MailDialog from './MailDialog';
import ProjectFiles from './ProjectFiles';

const fileNameRegexp = /[/\\?%*:|"<>]/g;

export default {
  name: 'CompanyFiles',

  components: { MailDialog, ProjectFiles },

  props: {
    company: {
      type: Object,
      required: true,
    },
  },

  data() {
    const fileStatuses = constants.companyFiles.statuses;

    return {
      selectedProjectId: null,
      fields: [
        { key: 'detailsToggle', class: 'cell-details-toggle' },
        { key: 'title', class: 'cell-title' },
        { key: 'status', class: 'cell-status' },
        { key: 'expires', class: 'cell-expires' },
        { key: 'divider', class: 'cell-divider' },
        { key: 'links', class: 'cell-links' },
      ],
      missingFilesItem: null,
      mailLinkItem: null,
      fileStatuses,
      fileStatusClasses: {
        [fileStatuses.NOT_LOADED]: 'file-status-not-loaded',
        [fileStatuses.FOR_APPROVAL]: 'file-status-for-approval',
        [fileStatuses.APPROVED]: 'file-status-approved',
        [fileStatuses.WRONG]: 'file-status-wrong',
      },
      fileStatusTitles: {
        [fileStatuses.NOT_LOADED]: this.$t(`fileStatus.${fileStatuses.NOT_LOADED}`),
        [fileStatuses.FOR_APPROVAL]: this.$t(`fileStatus.${fileStatuses.FOR_APPROVAL}`),
        [fileStatuses.APPROVED]: this.$t(`fileStatus.${fileStatuses.APPROVED}`),
        [fileStatuses.WRONG]: this.$t(`fileStatus.${fileStatuses.WRONG}`),
      },
      qrCodes: {},
      qrCodePromises: {},
    };
  },

  computed: {
    ...mapGetters(['getProject']),
    ...mapCompanyFilesGetters([getCompanyFilesByCompanyId]),

    companyId() {
      return (this.company && this.company._id) || null;
    },

    filesDoc() {
      return this.companyId ? this[getCompanyFilesByCompanyId](this.companyId) : null;
    },

    companyName() {
      return (this.company && this.company.companyName) || '';
    },

    registrationLinks() {
      return (this.company && this.company.registrationLinks) || [];
    },

    projectFiles() {
      return (this.filesDoc && this.filesDoc.projectFiles) || {};
    },

    projectIds() {
      const ids = this.registrationLinks.map(({ projectId }) => projectId);
      Object.keys(this.projectFiles).forEach((projectId) => {
        if (!ids.includes(projectId)) {
          ids.push(projectId);
        }
      });

      return ids;
    },

    items() {
      const today = moment().format(constants.formats.dateShort);
      const projects = this.projectIds.map((projectId) => this.getProject(projectId)).filter(Boolean);

      return projects.map((project) => {
        const link = this.registrationLinks.find(({ projectId }) => projectId === project._id);
        const files = this.projectFiles[project._id] && this.projectFiles[project._id].files;
        const worstStatus = this.getWorstFileStatus(files);

        const item = {
          // b-table's attribute. Controls whether row is expanded to show details or not.
          // We need to manually control it to allow only one row to be expanded at a time
          // and to preserve expanded state between saves.
          _showDetails: this.selectedProjectId === project._id,
          projectId: project._id,
          project,
          worstStatus,
          link,
          isLinkExpired: !!link && today > link.expiryDate,
          tooltip: {
            content: [project.postcode, project.title].filter(Boolean).join(' '),
            placement: 'top',
            offset: 20,
            delay: { show: 200, hide: 200 },
            classes: ['tooltip-bubble'],
          },
          linkTooltip: null,
        };

        if (link && (link.linkMailTo || link.lastMailedOn)) {
          item.linkTooltip = {
            content: link.linkMailTo
              ? this.$t('Mailing to {linkMailTo}', { linkMailTo: link.linkMailTo })
              : this.$t('Last mailed on {lastMailedOn}', {
                  lastMailedOn: moment(link.lastMailedOn).format(constants.formats.dateShort),
                }),
            placement: 'top',
            offset: 20,
            delay: { show: 200, hide: 200 },
            classes: ['tooltip-bubble'],
          };
        }

        return item;
      });
    },
  },

  methods: {
    ...mapActions(['mailCompanyMissingFilesReminder', 'mailCompanyRegistrationLink']),

    hasNotLoadedFiles(files) {
      return constants.companyFiles.names.some((fileName) => {
        const file = files.find((item) => item.fileName === fileName);
        return !file || file.status === this.fileStatuses.NOT_LOADED;
      });
    },

    hasFilesWithStatus(files, status) {
      return files.some((file) => status === file.status);
    },

    getWorstFileStatus(files) {
      if (!files || this.hasNotLoadedFiles(files)) {
        return this.fileStatuses.NOT_LOADED;
      }

      if (this.hasFilesWithStatus(files, this.fileStatuses.WRONG)) {
        return this.fileStatuses.WRONG;
      }

      if (this.hasFilesWithStatus(files, this.fileStatuses.FOR_APPROVAL)) {
        return this.fileStatuses.FOR_APPROVAL;
      }

      return this.fileStatuses.APPROVED;
    },

    toggleDetails(projectId) {
      this.selectedProjectId = this.selectedProjectId === projectId ? null : projectId;
    },

    onRowClick(item) {
      this.toggleDetails(item.projectId);
    },

    onStatusClick(item) {
      if (item.worstStatus === this.fileStatuses.NOT_LOADED) {
        this.missingFilesItem = item;
        this.openMissingFilesDialog();
      } else {
        this.toggleDetails(item.projectId);
      }
    },

    openMissingFilesDialog() {
      this.$modal.show('send-missing-files');
    },

    closeMissingFilesDialog() {
      this.$modal.hide('send-missing-files');
      this.missingFilesItem = null;
    },

    async onConfirmSendMissingFilesReminder(email) {
      try {
        if (this.missingFilesItem && this.missingFilesItem.projectId) {
          await this.mailCompanyMissingFilesReminder({
            company: this.company,
            email,
            projectIds: [this.missingFilesItem.projectId],
          });
        }
      } finally {
        this.closeMissingFilesDialog();
      }
    },

    onCancelSendMissingFilesReminder() {
      this.closeMissingFilesDialog();
    },

    getFullLink(link) {
      return `${window.location.origin}/${link}`;
    },

    onCopyLinkClick(link) {
      try {
        navigator.clipboard.writeText(this.getFullLink(link));
        EventBus.$emit('showMessageToUser', { message: 'Link copied', type: 'info', duration: 5000 });
      } catch (error) {
        console.error('Failed to copy link', error);
      }
    },

    onMailLinkClick(item) {
      this.openMailLinkDialog(item);
    },

    openMailLinkDialog(item) {
      if (item.link && !item.link.linkMailTo) {
        this.mailLinkItem = item;
        this.$modal.show('send-registration-link');
      }
    },

    closeMailLinkDialog() {
      this.$modal.hide('send-registration-link');
      this.mailLinkItem = null;
    },

    async onMailLinkConfirm(email) {
      try {
        if (this.mailLinkItem) {
          await this.mailCompanyRegistrationLink({ company: this.company, link: this.mailLinkItem.link, email });
        }
      } finally {
        this.closeMailLinkDialog();
      }
    },

    onMailLinkCancel() {
      this.closeMailLinkDialog();
    },

    async generateQrCode(uri) {
      try {
        const href = await QRCode.toDataURL(this.getFullLink(uri), { errorCorrectionLevel: 'H', margin: 4, scale: 8 });
        Object.values(this.qrCodes).forEach((qrCode) => {
          if (qrCode.uri === uri) {
            Vue.set(qrCode, 'href', href);
          }
        });
      } catch (error) {
        EventBus.$emit('showMessageToUser', {
          message: this.$t('Failed to generate QR code'),
          type: 'error',
          duration: 3000,
        });
        console.error(error);
      } finally {
        delete this.qrCodePromises[uri];
      }
    },
  },

  watch: {
    items: {
      immediate: true,
      handler(next) {
        const qrCodes = {};
        next.forEach(({ projectId, project, link }) => {
          const currentQrCode = this.qrCodes[projectId];
          const uri = link.link;
          if (currentQrCode && currentQrCode.uri === uri && currentQrCode.href) {
            qrCodes[projectId] = { ...currentQrCode };
            return;
          }

          const fileName = `Registration - ${this.companyName} - ${project.title}`.replaceAll(fileNameRegexp, '');
          qrCodes[projectId] = { uri, href: null, fileName: `${fileName}.png` };
          if (uri && !this.qrCodePromises[uri]) {
            this.qrCodePromises[uri] = this.generateQrCode(uri);
          }
        });

        this.qrCodes = qrCodes;
      },
    },
  },
};
</script>

<style scoped>
.projects-list >>> .card-body {
  padding: 0;
}

.projects-list >>> .card-body > h4 {
  padding: 0 15px;
}

.projects-table >>> .cell-details-toggle {
  width: 40px;
  min-width: 40px;
  text-align: center;
}

.projects-table >>> .cell-title {
  min-width: 120px;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.projects-table >>> .cell-divider {
  width: 2px;
}

.projects-table >>> .cell-divider .vertical-line {
  width: 2px;
  min-height: 2em;
  background-color: rgba(0, 0, 0, 0.125);
}

.projects-table >>> .cell-expires {
  width: 200px;
  min-width: 200px;
  text-align: right;
  padding-right: 2rem;
}

.projects-table >>> .cell-links {
  width: 100%;
  text-align: right;
  padding-right: 1.5rem;
}

.projects-table >>> tbody {
  border-top: 1px !important;
}

.projects-table >>> thead {
  display: none;
}

.projects-table >>> .projects-row:not(.b-table-details) {
  cursor: pointer;
}

.projects-table >>> .projects-row > td {
  vertical-align: middle;
}

/* Bootstrap's class */
.projects-table >>> .b-table-details > td {
  padding: 1rem;
}

.files-status {
  border-radius: 1.25rem;
  box-shadow: none;
  /* border-color: #a3a4a7; */
  /* box-shadow: 0px 3px 6px 0px #97979752; */
  border: none;
  padding: 6px 0;
  white-space: nowrap;
  line-height: 1.5em;
  width: 180px;
  min-width: 180px;
  text-align: center;
}

.files-status.file-status-not-loaded {
  box-shadow: 0px 3px 6px 0px #97979752;
}

.expiry-link {
  color: rgba(0, 0, 0, 0.25);
  font-size: 0.75rem;
  font-weight: 400;
}

.link-expired {
  color: rgba(var(--bs-danger-rgb), 0.6);
}

.link-icon {
  font-size: 0.875rem;
}
</style>



// WEBPACK FOOTER //
// src/components/organisation/EditCompany/CompanyFiles/index.vue